/**
 * Serum-launch bundle CTA — button-only quiz result.
 *
 * On Octane quiz completion this SUPPRESSES the Octane result rendering and
 * shows ONLY a "Build Your Custom Bundle" button that deep-links into the
 * bundle builder with the shopper's concern preselected:
 *     /pages/bundle-builder?hair_concern=<bucket>
 * sections/bb-hair-concerns.liquid reads that param and preselects the concern.
 *
 * This is a standalone script (does NOT render product recommendations) — see
 * quiz-dynamic.js / quiz-scripts.js for the full result-rendering handlers.
 *
 * PARKED — not shipping. Ali reviewed this flow and asked for a prebuilt-bundle
 * result page instead. The handle guard in layout/theme.liquid deliberately points
 * at a page that does not exist, so this never fires.
 *
 * It was previously gated on 'haircare-quiz-test'. That is now the live page for the
 * Aug 2026 hair-oil quiz, where this script was hiding the entire recommended routine
 * — the shopper saw only the heading and the button. If you want to preview this
 * again, point the guard at a scratch page handle, never a real quiz page.
 */
(function () {
  'use strict';

  var BUNDLE_PATH = '/pages/bundle-builder';
  var RESULT_SELECTOR = '.octane-ai-quiz-rendered';

  // Map quiz answers -> bundle-builder concern bucket
  // (thinning | lacks-volume | dry-damaged | frizzy | sensitive).
  // A sensitive scalp always wins; else the most specific concern maps, with
  // 'dry-damaged' as the catch-all. Returns '' when nothing resolves.
  function resolveBucket(concernRaw, scalpRaw) {
    var concern = (Array.isArray(concernRaw) ? concernRaw.join(',') : (concernRaw || '')).toString().toLowerCase();
    var scalp = (Array.isArray(scalpRaw) ? scalpRaw.join(',') : (scalpRaw || '')).toString().toLowerCase();
    if (scalp.indexOf('sensitive') > -1 || scalp.indexOf('prone to irritation') > -1) return 'sensitive';
    var rules = [
      ['thinning', ['thinning']],
      ['lacks-volume', ['lack of volume', 'lacks volume', 'volume', 'oily']],
      ['frizzy', ['frizz']],
      ['dry-damaged', ['dull', 'dry', 'breakage', 'split end', 'heat damage', 'damage', 'grey', 'knot', 'tangle', 'scalp']]
    ];
    for (var i = 0; i < rules.length; i++) {
      for (var j = 0; j < rules[i][1].length; j++) {
        if (concern.indexOf(rules[i][1][j]) > -1) return rules[i][0];
      }
    }
    return '';
  }

  // Answers are keyed by question title (e.g. "Octane: Hair Concern",
  // "Octane: Scalp Feel"); fuzzy-match the key by substring.
  function answerFor(answers, needle) {
    if (!answers) return '';
    needle = needle.toLowerCase();
    for (var k in answers) {
      if (Object.prototype.hasOwnProperty.call(answers, k) && k.toLowerCase().indexOf(needle) > -1) {
        return answers[k];
      }
    }
    return '';
  }

  var TITLE_SEL = '.oct-quiz-title, [data-element~="oct-quiz-title"]';
  var SUB_SEL = '.oct-quiz-subheading, [data-element~="oct-quiz-subheading"]';

  function buildButton(url) {
    var a = document.createElement('a');
    a.id = 'bb-quiz-cta';
    a.href = url;
    a.textContent = 'Build Your Custom Bundle →';
    a.style.cssText = 'display:inline-block;margin-top:1.75rem;padding:1rem 2.25rem;background:#3b332f;color:#fff;' +
      'text-decoration:none;border-radius:4px;font-weight:700;text-transform:uppercase;' +
      'letter-spacing:.03em;font-size:1rem;line-height:1.2;';
    return a;
  }

  // Grab the quiz's personalized copy block (the heading + subheading, e.g.
  // "…Your Personalized Haircare Routine Is Ready" / "Based on your hair
  // concerns — …") as a clone, so we can show it above our CTA while the live
  // CSS rule blankets the rest of the result (product recommendations etc.).
  function cloneCopy(container) {
    var contents = container.querySelectorAll('.oct-quiz-content');
    for (var i = 0; i < contents.length; i++) {
      if (contents[i].querySelector(TITLE_SEL + ',' + SUB_SEL) &&
          !contents[i].querySelector('.oct-quiz-product-block, .oct-quiz-products')) {
        var node = contents[i].cloneNode(true);
        node.removeAttribute('id');
        return node;
      }
    }
    // fallback: assemble from the title + subheading directly
    var t = container.querySelector(TITLE_SEL);
    var s = container.querySelector(SUB_SEL);
    if (!t && !s) return null;
    var frag = document.createElement('div');
    if (t) frag.appendChild(t.cloneNode(true));
    if (s) frag.appendChild(s.cloneNode(true));
    return frag;
  }

  // Keep the quiz copy + our CTA; hide everything else Octane paints (product
  // blocks, add-to-cart, etc.) via a live CSS rule that also covers late paints.
  function render(url) {
    var container = document.querySelector(RESULT_SELECTOR);
    if (!container) return false;
    if (!document.getElementById('bb-quiz-cta-style')) {
      var style = document.createElement('style');
      style.id = 'bb-quiz-cta-style';
      style.textContent =
        // hide everything Octane paints except our wrapper
        RESULT_SELECTOR + ' > *:not(#bb-quiz-cta-wrap){display:none !important;}' +
        // the cloned copy never receives Octane's entrance-reveal, so it's stuck at
        // opacity:0 — force it visible.
        '#bb-quiz-cta-wrap .bb-quiz-cta-copy, #bb-quiz-cta-wrap .bb-quiz-cta-copy *' +
        '{opacity:1 !important;transform:none !important;animation:none !important;filter:none !important;}';
      document.head.appendChild(style);
    }
    var wrap = document.getElementById('bb-quiz-cta-wrap');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.id = 'bb-quiz-cta-wrap';
      wrap.style.cssText = 'text-align:center;max-width:680px;margin:0 auto;padding:2.5rem 1rem 3.5rem;width:100%;';
      wrap.appendChild(buildButton(url));
      container.insertBefore(wrap, container.firstChild);
    }
    // Clone the quiz copy above the button once Octane has painted it.
    if (wrap.getAttribute('data-copy') !== '1') {
      var copy = cloneCopy(container);
      if (copy) {
        copy.className = (copy.className ? copy.className + ' ' : '') + 'bb-quiz-cta-copy';
        wrap.insertBefore(copy, wrap.firstChild);
        wrap.setAttribute('data-copy', '1');
      }
    }
    return true;
  }

  function onComplete(e) {
    try {
      var answers = e && e.detail && e.detail.answers;
      var bucket = resolveBucket(answerFor(answers, 'concern'), answerFor(answers, 'scalp'));
      var url = bucket ? (BUNDLE_PATH + '?hair_concern=' + encodeURIComponent(bucket)) : BUNDLE_PATH;
      if (window.console && console.log) { console.log('[bb-quiz-cta] bucket:', bucket || '(none)', '-> ', url); }
      // Show the button ASAP, then keep polling so the copy is cloned in once
      // Octane paints it. Stop when the copy is in place or after ~10s.
      render(url);
      var tries = 0;
      var timer = setInterval(function () {
        render(url);
        var w = document.getElementById('bb-quiz-cta-wrap');
        if ((w && w.getAttribute('data-copy') === '1') || ++tries > 40) { clearInterval(timer); }
      }, 250);
    } catch (err) { /* best-effort; never block the quiz */ }
  }

  document.addEventListener('octane.quiz.completed', onComplete);
  document.addEventListener('octane.quiz.result', onComplete);
})();
